"use client"

import React from "react"
import { classNames } from "@/lib/utils"

export type CardBrand = "visa" | "mastercard" | "american_express" | "amex" | "discover" | null

interface CardBrandIconsProps {
    activeBrand?: CardBrand
    size?: "sm" | "md" | "lg"
    mode?: "single" | "row"
    className?: string
    showLabels?: boolean
}

// Normalized brand mapping
// Evervault returns: visa | mastercard | american-express | discover | diners-club | jcb | unionpay
const normalizeBrand = (brand: string | null | undefined): CardBrand => {
    if (!brand) return null
    // Normalize: lowercase, replace spaces and hyphens with underscores for consistent matching
    const normalized = brand.toLowerCase().replace(/[\s\-]+/g, "_")
    // Handle American Express variants: "american-express", "american_express", "americanexpress", "amex"
    if (normalized === "american_express" || normalized === "americanexpress" || normalized === "amex") {
        return "amex"
    }
    if (["visa", "mastercard", "discover"].includes(normalized)) {
        return normalized as CardBrand
    }
    return null
}

// Size configurations
const sizeConfig = {
    sm: { width: 32, height: 20, className: "h-5" },
    md: { width: 40, height: 25, className: "h-6" },
    lg: { width: 48, height: 30, className: "h-7" }
}

// Individual card brand icons as components
const VisaIcon = ({ className }: { className?: string }) => (
    <svg className={classNames("w-auto overflow-hidden rounded-sm", className)} viewBox="0 0 36 24" aria-label="Visa">
        <rect width={36} height={24} fill="#1A1F71" rx={3} />
        <path
            fill="#fff"
            d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z"
        />
    </svg>
)

const MastercardIcon = ({ className }: { className?: string }) => (
    <svg className={classNames("w-auto overflow-hidden rounded-sm", className)} viewBox="0 0 36 24" aria-label="Mastercard">
        <rect width={36} height={24} rx={3} fill="#000" />
        <circle cx="13" cy="12" r="7" fill="#EB001B" />
        <circle cx="23" cy="12" r="7" fill="#F79E1B" />
        <path fill="#FF5F00" d="M18 17.5a7 7 0 010-11 7 7 0 010 11z" />
    </svg>
)

const AmexIcon = ({ className }: { className?: string }) => (
    <svg className={classNames("w-auto overflow-hidden rounded-sm", className)} viewBox="0 0 36 24" aria-label="American Express">
        <rect width={36} height={24} rx={3} fill="#2557D6" />
        <text x="18" y="10.5" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.3">AMERICAN</text>
        <text x="18" y="17" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.3">EXPRESS</text>
    </svg>
)

const DiscoverIcon = ({ className }: { className?: string }) => (
    <svg className={classNames("w-auto overflow-hidden rounded-sm", className)} viewBox="0 0 58 40" aria-label="Discover">
        <rect width="58" height="40" rx="4" fill="#F0F0F0" />
        <rect x="0.5" y="0.5" width="57" height="39" rx="3.5" fill="#F5F5F5" stroke="#D4D4D4" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.0689 20.3224C19.0689 22.7645 20.9865 24.658 23.4543 24.658C24.1518 24.658 24.7494 24.5209 25.4861 24.1739V22.2663C24.8384 22.9147 24.2644 23.1763 23.5297 23.1763C21.8975 23.1763 20.739 21.9928 20.739 20.3102C20.739 18.7152 21.9341 17.4569 23.4543 17.4569C24.2272 17.4569 24.8124 17.7327 25.4861 18.3916V16.485C24.7748 16.1242 24.19 15.9746 23.4925 15.9746C21.0372 15.9746 19.0689 17.9066 19.0689 20.3224ZM14.91 18.3046C14.91 18.7536 15.1954 18.9908 16.168 19.3507C18.0114 20.0251 18.5577 20.6224 18.5577 21.9426C18.5577 23.5508 17.3762 24.6707 15.6923 24.6707C14.459 24.6707 13.5622 24.1851 12.8156 23.0889L13.8626 22.0807C14.2356 22.8022 14.8583 23.189 15.6313 23.189C16.3545 23.189 16.8896 22.6902 16.8896 22.0171C16.8896 21.6682 16.7275 21.3687 16.4036 21.1572C16.2408 21.057 15.9179 20.9081 15.2833 20.684C13.7612 20.1364 13.2393 19.5503 13.2393 18.4057C13.2393 17.046 14.3605 16.0252 15.8306 16.0252C16.7416 16.0252 17.5752 16.3366 18.272 16.9461L17.424 18.0564C17.0019 17.5833 16.6026 17.3836 16.1173 17.3836C15.4188 17.3836 14.91 17.7814 14.91 18.3046ZM10.5846 24.4709H12.2053V16.1625H10.5846V24.4709ZM7.27202 22.4038C6.76294 22.8638 6.10138 23.0644 5.05432 23.0644H4.61938V17.57H5.05432C6.10138 17.57 6.73694 17.7575 7.27202 18.2422C7.83247 18.741 8.16951 19.5146 8.16951 20.3103C8.16951 21.108 7.83247 21.905 7.27202 22.4038ZM5.37884 16.1625H3V24.4706H5.36632C6.62459 24.4706 7.53299 24.174 8.33064 23.5115C9.27852 22.7274 9.83896 21.5452 9.83896 20.3225C9.83896 17.8705 8.00709 16.1625 5.37884 16.1625ZM36.1203 16.1625L38.3357 21.7432L40.5801 16.1625H42.3368L38.7476 24.6841H37.8754L34.3497 16.1625H36.1203ZM43.0697 24.4708H47.6656V23.0642H44.689V20.8215H47.5561V19.4142H44.689V17.5702H47.6656V16.1623H43.0697V24.4708ZM50.3576 19.9874H50.831C51.8662 19.9874 52.4148 19.5377 52.4148 18.7025C52.4148 17.894 51.8662 17.4712 50.8567 17.4712H50.3576V19.9874ZM51.1421 16.1622C53.0109 16.1622 54.0823 17.0597 54.0823 18.6152C54.0823 19.8873 53.4102 20.7225 52.1895 20.9706L54.8052 24.4707H52.8122L50.5691 21.133H50.3576V24.4707H48.7388V16.1622H51.1421Z"
            fill="#1D1D1B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M34.1585 22.7268C35.4738 20.67 34.8731 17.9376 32.8166 16.6224V16.6224C30.76 15.3072 28.0271 15.9076 26.7117 17.9644V17.9644C25.3967 20.0206 25.9977 22.7539 28.0542 24.0691V24.0691C30.1107 25.3843 32.8434 24.783 34.1585 22.7268Z"
            fill="url(#paint0_linear_discover)"
        />
        <defs>
            <linearGradient id="paint0_linear_discover" x1="36.5397" y1="19.0034" x2="29.0929" y2="14.241" gradientUnits="userSpaceOnUse">
                <stop stopColor="#F6A000" />
                <stop offset="0.623918" stopColor="#E47E02" />
                <stop offset="1" stopColor="#D36002" />
            </linearGradient>
        </defs>
    </svg>
)

// Default card icon for unknown brands
const DefaultCardIcon = ({ className }: { className?: string }) => (
    <svg className={classNames("w-auto overflow-hidden rounded-sm", className)} viewBox="0 0 36 24" aria-label="Credit Card">
        <rect width={36} height={24} rx={3} fill="#E5E7EB" />
        <rect x="4" y="6" width="28" height="4" rx="1" fill="#9CA3AF" />
        <rect x="4" y="14" width="12" height="2" rx="0.5" fill="#D1D5DB" />
        <rect x="4" y="18" width="8" height="2" rx="0.5" fill="#D1D5DB" />
    </svg>
)

// Map of brand to icon component
const brandIcons: Record<string, React.FC<{ className?: string }>> = {
    visa: VisaIcon,
    mastercard: MastercardIcon,
    amex: AmexIcon,
    american_express: AmexIcon,
    discover: DiscoverIcon
}

// Main component
export default function CardBrandIcons({ activeBrand, size = "sm", mode = "row", className }: CardBrandIconsProps) {
    const normalizedActive = normalizeBrand(activeBrand)
    const sizeClass = sizeConfig[size].className

    // All brand keys to display in row mode
    const allBrands: CardBrand[] = ["visa", "mastercard", "amex", "discover"]

    if (mode === "single") {
        // Single mode: only show active brand or default
        if (!normalizedActive) {
            return <DefaultCardIcon className={classNames(sizeClass, className)} />
        }
        const IconComponent = brandIcons[normalizedActive] || DefaultCardIcon
        return <IconComponent className={classNames(sizeClass, className)} />
    }

    // Row mode: show all brands when none detected, collapse to single when detected
    // Uses smooth width/opacity animation for elegant Stripe-like effect
    return (
        <div className={classNames("flex items-center", className)}>
            {allBrands.map((brand) => {
                const IconComponent = brandIcons[brand] || DefaultCardIcon
                const isActive = normalizedActive === brand
                const hasActiveBrand = normalizedActive !== null

                // When a brand is detected, smoothly collapse non-active brands
                const shouldShow = !hasActiveBrand || isActive

                return (
                    <div
                        key={brand}
                        className="overflow-hidden"
                        style={{
                            // Smooth width collapse: icon width when visible, 0 when hidden
                            width: shouldShow ? (size === "sm" ? 32 : size === "md" ? 40 : 48) : 0,
                            opacity: shouldShow ? 1 : 0,
                            transform: shouldShow ? "scale(1)" : "scale(0.8)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            // Gap between icons (only when multiple are visible)
                            marginRight: shouldShow && !hasActiveBrand ? 4 : 0
                        }}>
                        <div
                            className={classNames(
                                "transition-all duration-300 ease-out",
                                // When no brand detected, show at reduced opacity
                                !hasActiveBrand && "opacity-60"
                            )}>
                            <IconComponent className={sizeClass} />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// Export individual icons for use elsewhere
export { VisaIcon, MastercardIcon, AmexIcon, DiscoverIcon, DefaultCardIcon }
